var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, NotifyPropertyChanges, Property, Complex } from '@syncfusion/ej2-base';
import { remove, L10n, Internationalization, Event, isNullOrUndefined } from '@syncfusion/ej2-base';
import { Browser, EventHandler, Touch, Collection } from '@syncfusion/ej2-base';
import { SparklineBorder, SparklineTooltipSettings, ContainerArea, AxisSettings, Padding, SparklineMarkerSettings } from './model/base';
import { SparklineDataLabelSettings, RangeBandSettings } from './model/base';
import { Size, createSvg, RectOption, Rect, drawRectangle, getIdElement, withInBounds, removeElement } from './utils/helper';
import { SparklineRenderer } from './rendering/sparkline-renderer';
import { getThemeColor } from './utils/helper';
/* eslint-disable */
/**
 * Represents the Sparkline control.
 * ```html
 * <div id="sparkline"/>
 * <script>
 *   var sparkline = new Sparkline();
 *   sparkline.appendTo("#sparkline");
 * </script>
 * ```
 */
let Sparkline = class Sparkline extends Component {
    // Sparkline rendering starts from here.
    /**
     * Constructor for creating the Sparkline widget
     */
    constructor(options, element) {
        super(options, element);
        /** @private */
        this.isDevice = Browser.isDevice;
        /** @private */
        this.intervalDivs = [10, 5, 2, 1];
    }
    /**
     * Initializing pre-required values for sparkline.
     */
    preRender() {
        this.allowServerDataBinding = false;
        this.unWireEvents();
        this.trigger('load', { sparkline: this });
        this.sparkTheme = getThemeColor(this.theme);
        this.sparklineRenderer = new SparklineRenderer(this);
        this.createSVG();
        this.wireEvents();
        this.setCulture();
    }
    /**
     * Sparkline Elements rendering starting.
     */
    render() {
        // Sparkline rendering splitted into rendering and calculations
        this.sparklineRenderer.processDataManager();
        this.renderComplete();
        this.allowServerDataBinding = true;
    }
    /**
     * @private
     */
    processSparklineData() {
        this.sparklineRenderer.processData();
        this.renderSparkline();
        this.element.appendChild(this.svgObject);
        this.setSecondaryElementPosition();
        this.trigger('loaded', { sparkline: this });
    }
    /**
     * To render sparkline elements
     */
    renderSparkline() {
        // To render the sparkline elements
        this.renderBorder();
        this.createDiv();
        this.sparklineRenderer.renderSeries();
    }
    /**
     * Create secondary element for the tooltip
     */
    createDiv() {
        let tooltipDiv = document.createElement('div');
        tooltipDiv.id = this.element.id + '_Secondary_Element';
        tooltipDiv.style.position = 'relative';
        this.element.appendChild(tooltipDiv);
        this.element.style.display = 'block';
        this.element.style.position = 'relative';
    }
    /**
     * To set the left and top position for data label template for sparkline
     */
    setSecondaryElementPosition() {
        let element = getIdElement(this.element.id + '_Secondary_Element');
        if (!element) {
            return;
        }
        let rect = this.element.getBoundingClientRect();
        let svgRect = getIdElement(this.element.id + '_svg').getBoundingClientRect();
        element.style.left = Math.max(svgRect.left - rect.left, 0) + 'px';
        element.style.top = Math.max(svgRect.top - rect.top, 0) + 'px';
    }
    /**
     * @private
     * Render the sparkline border
     */
    renderBorder() {
        let width = this.containerArea.border.width;
        let borderRect;
        if (width > 0 || this.containerArea.background !== 'transparent') {
            borderRect = new RectOption(this.element.id + '_SparklineBorder', this.sparkTheme.background, this.containerArea.border, 1, new Rect(width / 2, width / 2, this.availableSize.width - width, this.availableSize.height - width));
            this.svgObject.appendChild(drawRectangle(this, borderRect));
        }
        // Used to create clip path sparkline
        let padding = this.padding;
        if (this.markerSettings.visible.length) {
            padding.left = 0;
            padding.right = 0;
            padding.bottom = 0;
            padding.top = 0;
        }
        borderRect = new RectOption(this.element.id + '_sparkline_clip_rect', 'transparent', { color: 'transparent', width: 0 }, 1, new Rect(padding.left, padding.top, this.availableSize.width - (padding.left + padding.right), this.availableSize.height - (padding.top + padding.bottom)));
        let clipPath = this.renderer.createClipPath({ id: this.element.id + '_sparkline_clip_path' });
        drawRectangle(this, borderRect, clipPath);
        this.svgObject.appendChild(clipPath);
    }
    /**
     * To create svg element for sparkline
     */
    createSVG() {
        this.removeSvg();
        createSvg(this);
    }
    /**
     * To Remove the Sparkline SVG object
     */
    removeSvg() {
        if (this.svgObject) {
            while (this.svgObject.childNodes.length > 0) {
                this.svgObject.removeChild(this.svgObject.firstChild);
            }
            if (!this.svgObject.hasChildNodes() && this.svgObject.parentNode) {
                remove(this.svgObject);
            }
        }
        removeElement(this.element.id + '_Secondary_Element');
        if (this.sparklineTooltipModule) {
            this.sparklineTooltipModule.removeTooltipElements();
        }
    }
    /**
     * Method to set culture for sparkline
     */
    setCulture() {
        this.intl = new Internationalization();
        this.localeObject = new L10n(this.getModuleName(), this.defaultLocalConstants, this.locale);
    }
    /**
     * To provide the array of modules needed for sparkline rendering
     * @return {ModuleDeclaration[]}
     * @private
     */
    requiredModules() {
        let modules = [];
        if (this.tooltipSettings.visible || this.tooltipSettings.trackLineSettings.visible) {
            modules.push({
                member: 'SparklineTooltip',
                args: [this]
            });
        }
        return modules;
    }
    /**
     * Method to unbind events for sparkline chart
     */
    unWireEvents() {
        let move = Browser.touchMoveEvent;
        let cancel = Browser.isPointer ? 'pointerleave' : 'mouseleave';
        /*! UnBind the Event handler */
        EventHandler.remove(this.element, Browser.touchMoveEvent, this.sparklineMove);
        EventHandler.remove(this.element, cancel, this.sparklineMouseLeave);
        EventHandler.remove(this.element, Browser.touchEndEvent, this.sparklineMouseEnd);
        window.removeEventListener((Browser.isTouch && ('orientation' in window && 'onorientationchange' in window)) ? 'orientationchange' : 'resize', this.sparklineResize);
    }
    /**
     * Method to bind events for the sparkline
     */
    wireEvents() {
        let cancel = Browser.isPointer ? 'pointerleave' : 'mouseleave';
        /*! Bind the Event handler */
        EventHandler.add(this.element, Browser.touchMoveEvent, this.sparklineMove, this);
        EventHandler.add(this.element, 'click', this.sparklineClick, this);
        EventHandler.add(this.element, cancel, this.sparklineMouseLeave, this);
        EventHandler.add(this.element, Browser.touchEndEvent, this.sparklineMouseEnd, this);
        window.addEventListener((Browser.isTouch && ('orientation' in window && 'onorientationchange' in window)) ? 'orientationchange' : 'resize', this.sparklineResize.bind(this));
        new Touch(this.element);
    }
    /**
     * Sparkline resize event.
     * @private
     */
    sparklineResize(e) {
        let args = {
            name: 'resize',
            previousSize: this.availableSize,
            sparkline: this,
            currentSize: new Size(0, 0)
        };
        if (this.resizeTo) {
            clearTimeout(this.resizeTo);
        }
        this.resizeTo = +setTimeout(() => {
            if (this.isDestroyed) {
                clearTimeout(this.resizeTo);
                return;
            }
            this.unWireEvents();
            this.createSVG();
            this.refreshing = true;
            this.wireEvents();
            args.currentSize = this.availableSize;
            this.trigger('resize', args);
            this.render();
        }, 500);
        return false;
    }
    /**
     * Handles the mouse move on sparkline.
     * @return {boolean}
     * @private
     */
    sparklineMove(e) {
        this.setSparklineMouseXY(e);
        this.notify(Browser.touchMoveEvent, e);
        let args = {
            name: 'sparklineMouseMove', cancel: false,
            sparkline: this, event: e
        };
        this.trigger(args.name, args);
        let pointClick = this.isPointRegion(e);
        if (pointClick.isPointRegion) {
            let pointArgs = {
                name: 'pointRegionMouseMove', cancel: false,
                event: e, sparkline: this,
                pointIndex: pointClick.pointIndex
            };
            this.trigger(pointArgs.name, pointArgs);
        }
        return false;
    }
    /**
     * Handles the mouse click on sparkline.
     * @return {boolean}
     * @private
     */
    sparklineClick(e) {
        this.setSparklineMouseXY(e);
        let args = {
            name: 'sparklineMouseClick', cancel: false,
            sparkline: this, event: e
        };
        this.trigger(args.name, args);
        let pointClick = this.isPointRegion(e);
        if (pointClick.isPointRegion) {
            let pointArgs = {
                name: 'pointRegionMouseClick', cancel: false,
                event: e, sparkline: this,
                pointIndex: pointClick.pointIndex
            };
            this.trigger(pointArgs.name, pointArgs);
        }
        return false;
    }
    /**
     * To check mouse event target is point region or not.
     */
    isPointRegion(e) {
        let startId = this.element.id + '_';
        let id = e.target.id.replace(startId, '').split('_');
        if (id[1] === this.type.toLowerCase()) {
            let index = parseInt(id[2], 10);
            if ((isNullOrUndefined(index) || isNaN(index)) && (this.type === 'Line' || this.type === 'Area')) {
                this.sparklineRenderer.visiblePoints.forEach((point, i) => {
                    if (withInBounds(this.mouseX, this.mouseY, new Rect(point.x - 5, point.y - 5, 10, 10))) {
                        index = i;
                        return;
                    }
                });
            }
            return { isPointRegion: true, pointIndex: index };
        }
        return { isPointRegion: false, pointIndex: null };
    }
    /**
     * Handles the mouse end.
     * @return {boolean}
     * @private
     */
    sparklineMouseEnd(e) {
        this.setSparklineMouseXY(e);
        this.notify(Browser.touchEndEvent, e);
        return false;
    }
    /**
     * Handles the mouse leave on sparkline.
     * @return {boolean}
     * @private
     */
    sparklineMouseLeave(e) {
        this.setSparklineMouseXY(e);
        this.notify(Browser.isPointer ? 'pointerleave' : 'mouseleave', e);
        return false;
    }
    /**
     * Method to set mouse x, y from events
     */
    setSparklineMouseXY(e) {
        let pageY;
        let pageX;
        if (e.type.indexOf('touch') > -1) {
            this.isTouch = true;
            let touchArg = e;
            pageX = touchArg.changedTouches[0].clientX;
            pageY = touchArg.changedTouches[0].clientY;
        }
        else {
            this.isTouch = e.pointerType === 'touch' || e.pointerType === '2';
            pageY = e.clientY;
            pageX = e.clientX;
        }
        let rect = this.element.getBoundingClientRect();
        let svgRect = getIdElement(this.element.id + '_svg').getBoundingClientRect();
        this.mouseY = (pageY - rect.top) - Math.max(svgRect.top - rect.top, 0);
        this.mouseX = (pageX - rect.left) - Math.max(svgRect.left - rect.left, 0);
    }
    /**
     * To change rendering while property value modified.
     * @private
     */
    onPropertyChanged(newProp, oldProp) {
        let render = false;
        let refresh = false;
        for (let prop of Object.keys(newProp)) {
            switch (prop) {
                case 'xName':
                case 'yName':
                case 'axisSettings':
                case 'rangeBandSettings':
                case 'type':
                case 'valueType':
                case 'enableRtl':
                    refresh = true;
                    break;
                case 'dataSource':
                    refresh = true;
                    break;
                case 'border':
                case 'markerSettings':
                case 'dataLabelSettings':
                case 'tooltipSettings':
                case 'startPointColor':
                case 'highPointColor':
                case 'lowPointColor':
                case 'endPointColor':
                case 'negativePointColor':
                case 'theme':
                    render = true;
                    break;
            }
        }
        if (refresh) {
            this.createSVG();
            this.sparklineRenderer.processData();
            this.refreshSparkline();
        }
        else if (render) {
            this.createSVG();
            this.refreshSparkline();
        }
    }
    /**
     * To render sparkline series and appending.
     */
    refreshSparkline() {
        // Issue fix. React had native render method. So OnProperty change used render method won't wrok. 
        this.renderSparkline();
        this.element.appendChild(this.svgObject);
        this.setSecondaryElementPosition();
    }
    /**
     * Get component name
     */
    getModuleName() {
        return 'sparkline';
    }
    /**
     * Destroy the component
     */
    destroy() {
        super.destroy();
    }
    /**
     * Get the properties to be maintained in the persisted state.
     * @private
     */
    getPersistData() {
        return '';
    }
};
__decorate([
    Property(null)
], Sparkline.prototype, "width", void 0);
__decorate([
    Property(null)
], Sparkline.prototype, "height", void 0);
__decorate([
    Complex({}, SparklineBorder)
], Sparkline.prototype, "border", void 0);
__decorate([
    Property('Line')
], Sparkline.prototype, "type", void 0);
__decorate([
    Property('None')
], Sparkline.prototype, "rangePadding", void 0);
__decorate([
    Property(null)
], Sparkline.prototype, "dataSource", void 0);
__decorate([
    Property(null)
], Sparkline.prototype, "query", void 0);
__decorate([
    Property('Numeric')
], Sparkline.prototype, "valueType", void 0);
__decorate([
    Property(null)
], Sparkline.prototype, "xName", void 0);
__decorate([
    Property(null)
], Sparkline.prototype, "yName", void 0);
__decorate([
    Property('#00bdae')
], Sparkline.prototype, "fill", void 0);
__decorate([
    Property('')
], Sparkline.prototype, "highPointColor", void 0);
__decorate([
    Property('')
], Sparkline.prototype, "lowPointColor", void 0);
__decorate([
    Property('')
], Sparkline.prototype, "startPointColor", void 0);
__decorate([
    Property('')
], Sparkline.prototype, "endPointColor", void 0);
__decorate([
    Property('')
], Sparkline.prototype, "negativePointColor", void 0);
__decorate([
    Property('')
], Sparkline.prototype, "tiePointColor", void 0);
__decorate([
    Property([])
], Sparkline.prototype, "palette", void 0);
__decorate([
    Property(1)
], Sparkline.prototype, "lineWidth", void 0);
__decorate([
    Property(1)
], Sparkline.prototype, "opacity", void 0);
__decorate([
    Property(null)
], Sparkline.prototype, "format", void 0);
__decorate([
    Property(false)
], Sparkline.prototype, "useGroupingSeparator", void 0);
__decorate([
    Complex({}, SparklineTooltipSettings)
], Sparkline.prototype, "tooltipSettings", void 0);
__decorate([
    Complex({}, ContainerArea)
], Sparkline.prototype, "containerArea", void 0);
__decorate([
    Collection([], RangeBandSettings)
], Sparkline.prototype, "rangeBandSettings", void 0);
__decorate([
    Complex({}, AxisSettings)
], Sparkline.prototype, "axisSettings", void 0);
__decorate([
    Complex({}, SparklineMarkerSettings)
], Sparkline.prototype, "markerSettings", void 0);
__decorate([
    Complex({}, SparklineDataLabelSettings)
], Sparkline.prototype, "dataLabelSettings", void 0);
__decorate([
    Complex({}, Padding)
], Sparkline.prototype, "padding", void 0);
__decorate([
    Property('Material')
], Sparkline.prototype, "theme", void 0);
__decorate([
    Event()
], Sparkline.prototype, "loaded", void 0);
__decorate([
    Event()
], Sparkline.prototype, "load", void 0);
__decorate([
    Event()
], Sparkline.prototype, "tooltipInitialize", void 0);
__decorate([
    Event()
], Sparkline.prototype, "seriesRendering", void 0);
__decorate([
    Event()
], Sparkline.prototype, "axisRendering", void 0);
__decorate([
    Event()
], Sparkline.prototype, "pointRendering", void 0);
__decorate([
    Event()
], Sparkline.prototype, "pointRegionMouseMove", void 0);
__decorate([
    Event()
], Sparkline.prototype, "pointRegionMouseClick", void 0);
__decorate([
    Event()
], Sparkline.prototype, "sparklineMouseMove", void 0);
__decorate([
    Event()
], Sparkline.prototype, "sparklineMouseClick", void 0);
__decorate([
    Event()
], Sparkline.prototype, "dataLabelRendering", void 0);
__decorate([
    Event()
], Sparkline.prototype, "markerRendering", void 0);
__decorate([
    Event()
], Sparkline.prototype, "resize", void 0);
Sparkline = __decorate([
    NotifyPropertyChanges
], Sparkline);
export { Sparkline };
